/* global globalThis */
import { optionalTrainingMetadata, validateTrainingMetadata } from './metadata';
import trainingGroups from '../../../functions/shared/trainingGroups';

// The same pure group rules are used before browser/server writes and by the
// legacy read projection. Existing records remain readable without a migration.
export const { MAX_GROUP_ROUNDS, groupRoundCount, groupIssues, samePrescriptions,
  normalizeWorkoutGroups, normalizePlanGroups, validateGroupStructure } = trainingGroups;

export const TRAINING_MODULE = 'allenamenti-trainer';
export const TRAINING_SCHEMA_VERSION = 1;
export const MAX_PLAN_BYTES = 700000;

const SET_FIELDS = ['reps', 'load', 'duration', 'rest', 'speed', 'incline'];
const EXERCISE_FIELDS = ['libraryId', 'name', 'equipment', 'type', 'description', 'videoUrl', 'notes', 'groupId'];
const GROUP_FIELDS = ['type', 'name', 'rounds', 'rest'];
const asText = value => value == null ? '' : String(value);
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const makeId = prefix => `${prefix}_${globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`}`;

const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const pad = value => String(value).padStart(2, '0');

export function isValidTrainingDate(value) {
  if (typeof value !== 'string' || !/^[1-9]\d{3}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function monthPeriod(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const dateText = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return { periodName: `${MONTH_NAMES[first.getMonth()]} ${first.getFullYear()}`, startDate: dateText(first), endDate: dateText(last) };
}

// Legacy records acquire period fields in memory only; reading never migrates Firestore.
function periodFields(plan) {
  let createdAt = plan.createdAt;
  try { if (typeof createdAt?.toDate === 'function') createdAt = createdAt.toDate(); } catch { createdAt = null; }
  const fallback = createdAt instanceof Date && !Number.isNaN(createdAt.getTime())
    ? monthPeriod(createdAt.getFullYear(), createdAt.getMonth())
    : { periodName: 'Allenamento precedente', startDate: '', endDate: '' };
  return Object.fromEntries(['periodName', 'startDate', 'endDate'].map(field => [field, plan[field] === undefined ? fallback[field] : plan[field]]));
}

export function isValidUserId(userId) {
  return typeof userId === 'string' && userId.length > 0 && userId.length <= 128 &&
    userId === userId.trim() && !/[\s/]/.test(userId) &&
    !Array.from(userId).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) &&
    userId !== '.' && userId !== '..';
}

export function createSet(seed = {}) {
  return { ...Object.fromEntries([['id', makeId('set')], ...SET_FIELDS.map(field => [field, asText(seed[field])])]), ...optionalTrainingMetadata(seed, ['extraParameters']) };
}

export function createExercise(seed = {}) {
  return {
    id: makeId('exercise'),
    libraryId: asText(seed.libraryId ?? seed.id),
    name: asText(seed.name),
    equipment: asText(seed.equipment),
    type: ['reps', 'time', 'cardio'].includes(seed.type) ? seed.type : 'reps',
    description: asText(seed.description),
    videoUrl: asText(seed.videoUrl),
    notes: asText(seed.notes),
    groupId: '',
    sets: Array.isArray(seed.sets) && seed.sets.length ? seed.sets.map(createSet) : [createSet()],
    ...optionalTrainingMetadata(seed, ['details']),
  };
}

export function createWorkout(index = 0) {
  const position = Number.isInteger(index) && index >= 0 ? index : 0;
  return { id: makeId('workout'), name: `Workout ${position < 26 ? String.fromCharCode(65 + position) : position + 1}`, notes: '', exercises: [], groups: [] };
}

export function createPlan(userId) {
  const today = new Date();
  return {
    schemaVersion: TRAINING_SCHEMA_VERSION,
    module: TRAINING_MODULE,
    userId,
    title: 'Programma di allenamento',
    status: 'draft',
    notes: '',
    ...monthPeriod(today.getFullYear(), today.getMonth()),
    workouts: [createWorkout()],
    revision: 0,
    createdAt: null,
    createdBy: '',
    updatedAt: null,
    updatedBy: '',
  };
}

export function duplicateExercise(exercise) {
  return createExercise(exercise);
}

export function duplicateWorkout(workout) {
  const groupIds = new Map();
  const groups = (workout.groups || []).map(group => {
    const id = makeId('group');
    groupIds.set(group.id, id);
    return { id, ...Object.fromEntries(GROUP_FIELDS.map(field => [field, asText(group[field])])) };
  });
  return {
    id: makeId('workout'),
    name: `${asText(workout.name).slice(0, 152)} (copia)`,
    notes: asText(workout.notes),
    ...optionalTrainingMetadata(workout, ['levelId', 'summary']),
    groups,
    exercises: (workout.exercises || []).map(exercise => ({
      ...duplicateExercise(exercise),
      groupId: groupIds.get(exercise.groupId) || '',
    })),
  };
}

export function createNextTrainingPlan(source, userId = source.userId) {
  // Validate before cloning so malformed source data cannot be silently repaired.
  const original = serializePlan(source);
  const next = createPlan(userId);
  const today = new Date();
  const [year, month] = isValidTrainingDate(original.startDate)
    ? original.startDate.split('-').slice(0, 2).map(Number)
    : [today.getFullYear(), today.getMonth() + 1];
  const result = {
    ...next,
    title: original.title,
    notes: original.notes,
    ...optionalTrainingMetadata(original, ['details', 'source']),
    ...monthPeriod(year, month),
    workouts: original.workouts.map(workout => ({ ...duplicateWorkout(workout), name: workout.name })),
  };
  serializePlan(result);
  return normalizePlanGroups(result, { mode: 'safe' }).plan;
}

// One whitelist is shared by dirty tracking and persistence. Metadata is owned by the store.
function projectPlan(plan, trim) {
  const text = value => trim && typeof value === 'string' ? value.trim() : value;
  const fields = (source, keys) => Object.fromEntries(keys.map(key => [key,
    ['notes', 'description'].includes(key) ? source[key] : text(source[key]),
  ]));
  return {
    schemaVersion: plan.schemaVersion,
    module: plan.module,
    userId: plan.userId,
    title: text(plan.title),
    status: plan.status,
    notes: plan.notes,
    ...optionalTrainingMetadata(plan, ['details', 'source']),
    ...Object.fromEntries(Object.entries(periodFields(plan)).map(([key, value]) => [key, text(value)])),
    workouts: (plan.workouts || []).map(workout => ({
      ...fields(workout, ['id', 'name', 'notes']),
      ...optionalTrainingMetadata(workout, ['levelId', 'summary']),
      groups: (workout.groups || []).map(group => fields(group, ['id', ...GROUP_FIELDS])),
      exercises: (workout.exercises || []).map(exercise => ({
        ...fields(exercise, ['id', ...EXERCISE_FIELDS]),
        ...optionalTrainingMetadata(exercise, ['details']),
        sets: (exercise.sets || []).map(set => ({ ...fields(set, ['id', ...SET_FIELDS]), ...optionalTrainingMetadata(set, ['extraParameters']) })),
      })),
    })),
  };
}

export function planSignature(plan) {
  return plan ? JSON.stringify(projectPlan(plan, false)) : '';
}

export function validatePlan(plan) {
  const errors = [];
  if (!isRecord(plan)) return ['Il programma non è valido.'];
  errors.push(...validateTrainingMetadata(plan));
  if (plan.module !== TRAINING_MODULE || plan.schemaVersion !== TRAINING_SCHEMA_VERSION) errors.push('Formato del programma non supportato.');
  if (!isValidUserId(plan.userId)) errors.push('Il riferimento al cliente non è valido.');

  const text = (value, label, maxLength, required = false) => {
    if (typeof value !== 'string') {
      errors.push(`${label}: il valore deve essere un testo.`);
      return false;
    }
    if (required && !value.trim()) errors.push(`${label}: inserisci un valore.`);
    if (value.length > maxLength) errors.push(`${label}: massimo ${maxLength} caratteri.`);
    return true;
  };
  const numeric = (value, label, { integer = false, positive = false } = {}) => {
    if (!text(value, label, 24) || !value.trim()) return;
    const normalized = value.trim().replace(',', '.');
    if (!/^\d+(?:\.\d+)?$/.test(normalized) || !Number.isFinite(Number(normalized)) ||
      Number(normalized) > 100000 || (positive && Number(normalized) <= 0) ||
      (integer && !Number.isInteger(Number(normalized)))) {
      errors.push(`${label}: inserisci un numero ${integer ? 'intero ' : ''}${positive ? 'maggiore di zero' : 'uguale o maggiore di zero'}, fino a 100000.`);
    }
  };
  const seenIds = new Set();
  const id = (value, label) => {
    if (typeof value !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(value)) errors.push(`${label}: identificativo non valido.`);
    else if (seenIds.has(value)) errors.push(`${label}: identificativo duplicato.`);
    else seenIds.add(value);
  };
  text(plan.title, 'Nome programma', 160, true);
  text(plan.notes, 'Note programma', 4000);
  const period = periodFields(plan);
  text(period.periodName, 'Nome periodo', 160, true);
  ['startDate', 'endDate'].forEach(field => {
    const label = field === 'startDate' ? 'Data inizio' : 'Data fine';
    if (text(period[field], label, 10) && period[field] !== '' && !isValidTrainingDate(period[field])) errors.push(`${label}: usa una data valida nel formato AAAA-MM-GG.`);
  });
  if (isValidTrainingDate(period.startDate) && isValidTrainingDate(period.endDate) && period.endDate < period.startDate) errors.push('La data fine non può precedere la data inizio.');
  if (!['draft', 'ready'].includes(plan.status)) errors.push('Stato del programma non valido.');
  if (!Array.isArray(plan.workouts) || plan.workouts.length < 1 || plan.workouts.length > 24) {
    errors.push('Il programma deve contenere da 1 a 24 workout.');
    return errors;
  }
  let exerciseCount = 0;
  let setCount = 0;
  plan.workouts.forEach((workout, workoutIndex) => {
    const label = `Workout ${workoutIndex + 1}`;
    if (!isRecord(workout)) { errors.push(`${label}: dati non validi.`); return; }
    id(workout.id, label);
    text(workout.name, `${label}, nome`, 160, true);
    text(workout.notes, `${label}, note`, 4000);
    if (!Array.isArray(workout.groups) || workout.groups.length > 30) { errors.push(`${label}: massimo 30 gruppi.`); return; }
    if (!Array.isArray(workout.exercises) || workout.exercises.length > 60) { errors.push(`${label}: massimo 60 esercizi.`); return; }
    if (plan.status === 'ready' && !workout.exercises.length) errors.push(`${label}: aggiungi almeno un esercizio prima di segnare il programma come pronto.`);
    exerciseCount += workout.exercises.length;
    const groups = new Map();
    workout.groups.forEach(group => {
      if (!isRecord(group)) { errors.push(`${label}: gruppo non valido.`); return; }
      id(group.id, `${label}, gruppo`);
      text(group.name, `${label}, nome gruppo`, 160, true);
      if (!['circuit', 'superset'].includes(group.type)) errors.push(`${label}: tipo di gruppo non valido.`);
      numeric(group.rounds, `${label}, giri del gruppo`, { integer: true, positive: true });
      numeric(group.rest, `${label}, recupero del gruppo`);
      groups.set(group.id, { ...group, positions: [] });
    });
    workout.exercises.forEach((exercise, exerciseIndex) => {
      const exerciseLabel = `${label}, esercizio ${exerciseIndex + 1}`;
      if (!isRecord(exercise)) { errors.push(`${exerciseLabel}: dati non validi.`); return; }
      id(exercise.id, exerciseLabel);
      text(exercise.libraryId, `${exerciseLabel}, riferimento biblioteca`, 160);
      text(exercise.name, `${exerciseLabel}, nome`, 160, true);
      text(exercise.equipment, `${exerciseLabel}, attrezzatura`, 200);
      text(exercise.description, `${exerciseLabel}, descrizione`, 4000);
      text(exercise.notes, `${exerciseLabel}, note`, 4000);
      if (!['reps', 'time', 'cardio'].includes(exercise.type)) errors.push(`${exerciseLabel}: tipo non valido.`);
      if (text(exercise.videoUrl, `${exerciseLabel}, video`, 2000) && exercise.videoUrl.trim()) {
        try {
          const url = new URL(exercise.videoUrl.trim());
          if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) throw new Error('Invalid video URL');
        } catch { errors.push(`${exerciseLabel}: il video deve avere un indirizzo HTTPS valido.`); }
      }
      text(exercise.groupId, `${exerciseLabel}, riferimento gruppo`, 128);
      if (exercise.groupId) {
        if (!groups.has(exercise.groupId)) errors.push(`${exerciseLabel}: il gruppo non esiste in questo workout.`);
        else groups.get(exercise.groupId).positions.push(exerciseIndex);
      }
      if (!Array.isArray(exercise.sets) || exercise.sets.length < 1 || exercise.sets.length > 30) {
        errors.push(`${exerciseLabel}: inserisci da 1 a 30 serie.`);
        return;
      }
      setCount += exercise.sets.length;
      exercise.sets.forEach((set, setIndex) => {
        const setLabel = `${exerciseLabel}, serie ${setIndex + 1}`;
        if (!isRecord(set)) { errors.push(`${setLabel}: dati non validi.`); return; }
        id(set.id, setLabel);
        text(set.reps, `${setLabel}, ripetizioni`, 80);
        text(set.load, `${setLabel}, carico`, 80);
        ['duration', 'rest', 'speed', 'incline'].forEach(field => {
          const labels = { duration: 'durata', rest: 'recupero', speed: 'velocità', incline: 'pendenza' };
          numeric(set[field], `${setLabel}, ${labels[field]}`);
        });
      });
    });
    groups.forEach(group => {
      const count = group.positions.length;
      if (count < 2) errors.push(`${label}, ${group.name || 'gruppo'}: ${group.type === 'superset' ? 'la superserie richiede almeno due esercizi' : 'il circuito richiede almeno due esercizi'}.`);
      if (count > 1 && group.positions[count - 1] - group.positions[0] + 1 !== count) errors.push(`${label}, ${group.name || 'gruppo'}: gli esercizi del gruppo devono essere consecutivi.`);
    });
  });
  if (exerciseCount > 500) errors.push('Il programma può contenere al massimo 500 esercizi.');
  if (setCount > 5000) errors.push('Il programma può contenere al massimo 5000 serie.');
  if (!errors.length && new TextEncoder().encode(JSON.stringify(projectPlan(plan, true))).length > MAX_PLAN_BYTES) {
    errors.push('Il programma è troppo grande. Riduci le note, i workout o le serie.');
  }
  return errors;
}

export function serializePlan(plan) {
  const details = validatePlan(plan);
  if (details.length) {
    const error = new Error(details[0]);
    error.code = 'training/validation';
    error.details = details;
    throw error;
  }
  return projectPlan(plan, true);
}

export function validatePlanForWrite(plan) {
  return [...new Set([...validatePlan(plan), ...validateGroupStructure(plan?.workouts)])];
}

export function serializePlanForWrite(plan) {
  const details = validatePlanForWrite(plan);
  if (details.length) {
    throw Object.assign(new Error(details[0]), { code: 'training/validation', details });
  }
  return projectPlan(plan, true);
}
